import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS, isSuperAdmin, isFinanceAdmin, isAdmin, isMember, isDeveloper } from '../utils/permissions';

export const usePermissions = () => {
  const { user } = useAuth();
  
  const hasPermission = (permission: string): boolean => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };
  
  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user || !user.permissions) return false;
    return permissions.some(permission => user.permissions.includes(permission));
  };
  
  const canManageUsers = (): boolean => {
    return hasPermission(PERMISSIONS.MANAGE_USERS);
  };
  
  const canApproveFinances = (): boolean => {
    return hasPermission(PERMISSIONS.APPROVE_FINANCES);
  };
  
  const canViewAuditLogs = (): boolean => {
    return hasAnyPermission([PERMISSIONS.VIEW_AUDIT_LOGS, PERMISSIONS.MANAGE_USERS]);
  };
  
  const canManageMembers = (): boolean => {
    return hasAnyPermission([PERMISSIONS.ADD_MEMBERS, PERMISSIONS.EDIT_MEMBERS, PERMISSIONS.MANAGE_USERS]);
  };
  
  const canSendSMS = (): boolean => {
    return hasPermission(PERMISSIONS.SEND_SMS);
  };
  
  const canViewReports = (): boolean => {
    return hasAnyPermission([PERMISSIONS.VIEW_REPORTS, PERMISSIONS.MANAGE_USERS]);
  };
  
  const canApproveBenefits = (): boolean => {
    return hasPermission(PERMISSIONS.APPROVE_BENEFITS);
  };
  
  const canAccessSystem = (): boolean => {
    return hasPermission(PERMISSIONS.SYSTEM_ACCESS);
  };
  
  const canManageSystem = (): boolean => {
    return hasPermission(PERMISSIONS.MANAGE_SYSTEM);
  };
  
  const canViewBenefits = (): boolean => {
    return hasAnyPermission([PERMISSIONS.VIEW_BENEFITS, PERMISSIONS.APPROVE_BENEFITS, PERMISSIONS.VIEW_FINANCES]);
  };
  
  const isUserSuperAdmin = (): boolean => {
    return isSuperAdmin(user);
  };
  
  const isUserFinanceAdmin = (): boolean => {
    return isFinanceAdmin(user);
  };
  
  const isUserAdmin = (): boolean => {
    return isAdmin(user);
  };
  
  const isUserMember = (): boolean => {
    return isMember(user);
  };
  
  const isUserDeveloper = (): boolean => {
    return isDeveloper(user);
  };
  
  const isTownAdmin = (): boolean => {
    return user?.role === 'town_admin';
  };
  
  const isMemberAdmin = (): boolean => {
    return user?.role === 'member_admin';
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    canManageUsers,
    canApproveFinances,
    canApproveBenefits,
    canViewAuditLogs,
    canManageMembers,
    canSendSMS,
    canViewReports,
    canAccessSystem,
    canManageSystem,
    canViewBenefits,
    isSuperAdmin: isUserSuperAdmin,
    isFinanceAdmin: isUserFinanceAdmin,
    isAdmin: isUserAdmin,
    isMember: isUserMember,
    isDeveloper: isUserDeveloper,
    isTownAdmin,
    isMemberAdmin,
    userPermissions: user?.permissions || [],
    userTownId: user?.townId,
    userMemberId: user?.memberId,
    userRole: user?.role
  };
};